import { Document } from '@/modules/document/models/Document';
import { DocumentSubType } from '@/modules/document/enums/DocumentSubType';
import { DocumentType } from '@/modules/document/enums/DocumentType';
import { ModelCollection } from '@/modules/core/app/models/ModelCollection';

export class DocumentCollection extends ModelCollection {
  /**
   * @param {array} items
   */
  constructor(items = null) {
    super(items, Document);
  }

  /**
   *
   * @param {string} type
   * @returns {number}
   */
  getCountByType(type) {
    return this.get()?.filter((document) => document.type === type).length ?? 0;
  }

  /**
   * @param {string} typeKey
   * @param {number} userId
   * @returns {number}
   */
  getUnreadCount(typeKey, userId) {
    let unreadCount = 0;
    const items = this.get();
    if (items?.length) {
      items
        .filter((document) => document.type === typeKey)
        .forEach((document) => {
          unreadCount +=
            document.readReceipts?.filter(
              (readReceipt) => readReceipt.userId === userId && readReceipt.readAt === null
            ).length ?? 0;
        });
    }

    return unreadCount;
  }

  /**
   * @param {array|DocumentCollection|null} items
   * @returns {DocumentCollection}
   */
  static mapModel(items = null) {
    return new this(items, Document);
  }

  /**
   * @returns {?Document}
   */
  get identificationDocument() {
    return this.getValueForType(DocumentType.IDENTIFICATION.key);
  }

  /**
   * @param {Document} value
   */
  set identificationDocument(value) {
    this.setValueByType(value);
  }

  /**
   * @returns {?Document}
   */
  get identificationDriversLicense() {
    return this.getValueForSubType(DocumentSubType.IDENTIFICATION_DRIVERS_LICENSE.key);
  }

  /**
   * @param {Document} value
   */
  set identificationDriversLicense(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get identificationPassport() {
    return this.getValueForSubType(DocumentSubType.IDENTIFICATION_PASSPORT.key);
  }

  /**
   * @param {Document} value
   */
  set identificationPassport(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estateInventoryAndAppraisalForm() {
    return this.getValueForSubType([DocumentSubType.ESTATE_INVENTORY_AND_APPRAISAL_FORM.key]);
  }

  /**
   * @returns {?Document}
   */
  get estateInformationReturnForm() {
    return this.getValueForSubType([DocumentSubType.ESTATE_INFORMATION_RETURN.key]);
  }

  /**
   * @returns {?Document}
   */
  set estateInformationReturnForm(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  set estateInventoryAndAppraisalForm(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estateFormInstructions() {
    return this.getValueForSubType([DocumentSubType.ESTATE_FORM_INSTRUCTIONS.key]);
  }

  /**
   * @param {Document} value
   */
  set estateFormInstructions(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estatePowerOfAttorneyHealthcareDirective() {
    return this.getValueForSubType([
      DocumentSubType.ESTATE_POWER_OF_ATTORNEY_HEALTHCARE_DIRECTIVE.key
    ]);
  }

  /**
   * @param {Document} value
   */
  set estatePowerOfAttorneyHealthcareDirective(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estatePowerOfAttorneyHealthcareDirectiveSigned() {
    return this.getValueForSubType([
      DocumentSubType.ESTATE_POWER_OF_ATTORNEY_HEALTHCARE_DIRECTIVE_SIGNED.key
    ]);
  }

  /**
   * @param {Document} value
   */
  set estatePowerOfAttorneyHealthcareDirectiveSigned(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estatePowerOfAttorneyPersonalCare() {
    return this.getValueForSubType([DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PERSONAL_CARE.key]);
  }

  /**
   * @param {Document} value
   */
  set estatePowerOfAttorneyPersonalCare(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estatePowerOfAttorneyPersonalCareDocx() {
    return this.getValueForSubType([
      DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PERSONAL_CARE_DOCX.key
    ]);
  }

  /**
   * @param {Document} value
   */
  set estatePowerOfAttorneyPersonalCareDocx(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estatePowerOfAttorneyPersonalCareEstateProfessionalEdited() {
    return this.getValueForSubType([
      DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PERSONAL_CARE_ESTATE_PROFESSIONAL_EDITED.key
    ]);
  }

  /**
   * @param {Document} value
   */
  set estatePowerOfAttorneyPersonalCareEstateProfessionalEdited(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estatePowerOfAttorneyPersonalCarePartnerEdited() {
    return this.getValueForSubType([
      DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PERSONAL_CARE_PARTNER_EDITED.key
    ]);
  }

  /**
   * @param {Document} value
   */
  set estatePowerOfAttorneyPersonalCarePartnerEdited(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estatePowerOfAttorneyPersonalCareFinalUploaded() {
    return this.getValueForSubType([
      DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PERSONAL_CARE_FINAL_UPLOADED.key
    ]);
  }

  /**
   * @param {Document} value
   */
  set estatePowerOfAttorneyPersonalCareFinalUploaded(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estatePowerOfAttorneyPersonalCareSigned() {
    return this.getValueForSubType([
      DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PERSONAL_CARE_SIGNED.key
    ]);
  }

  /**
   * @param {Document} value
   */
  set estatePowerOfAttorneyPersonalCareSigned(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estatePowerOfAttorneyProperty() {
    return this.getValueForSubType([DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PROPERTY.key]);
  }

  /**
   * @param {Document} value
   */
  set estatePowerOfAttorneyProperty(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estatePowerOfAttorneyPropertyDocx() {
    return this.getValueForSubType([DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PROPERTY_DOCX.key]);
  }

  /**
   * @param {Document} value
   */
  set estatePowerOfAttorneyPropertyDocx(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estatePowerOfAttorneyPropertyEstateProfessionalEdited() {
    return this.getValueForSubType([
      DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PROPERTY_ESTATE_PROFESSIONAL_EDITED.key
    ]);
  }

  /**
   * @param {Document} value
   */
  set estatePowerOfAttorneyPropertyEstateProfessionalEdited(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estatePowerOfAttorneyPropertyPartnerEdited() {
    return this.getValueForSubType([
      DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PROPERTY_PARTNER_EDITED.key
    ]);
  }

  /**
   * @param {Document} value
   */
  set estatePowerOfAttorneyPropertyPartnerEdited(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estatePowerOfAttorneyPropertyFinalUploaded() {
    return this.getValueForSubType([
      DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PROPERTY_FINAL_UPLOADED.key
    ]);
  }

  /**
   * @param {Document} value
   */
  set estatePowerOfAttorneyPropertyFinalUploaded(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estatePowerOfAttorneyPropertySigned() {
    return this.getValueForSubType([DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PROPERTY_SIGNED.key]);
  }

  /**
   * @param {Document} value
   */
  set estatePowerOfAttorneyPropertySigned(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estatePowerOfAttorneyPropertyAndPersonalCare() {
    return this.getValueForSubType([
      DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PROPERTY_AND_PERSONAL_CARE.key
    ]);
  }

  /**
   * @param {Document} value
   */
  set estatePowerOfAttorneyPropertyAndPersonalCare(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estatePowerOfAttorneyPropertyAndPersonalCareDocx() {
    return this.getValueForSubType([
      DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PROPERTY_AND_PERSONAL_CARE_DOCX.key
    ]);
  }

  /**
   * @param {Document} value
   */
  set estatePowerOfAttorneyPropertyAndPersonalCareDocx(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estatePowerOfAttorneyPropertyAndPersonalCareEstateProfessionalEdited() {
    return this.getValueForSubType([
      DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PROPERTY_AND_PERSONAL_CARE_ESTATE_PROFESSIONAL_EDITED
        .key
    ]);
  }

  /**
   * @param {Document} value
   */
  set estatePowerOfAttorneyPropertyAndPersonalCareEstateProfessionalEdited(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estatePowerOfAttorneyPropertyAndPersonalCarePartnerEdited() {
    return this.getValueForSubType([
      DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PROPERTY_AND_PERSONAL_CARE_PARTNER_EDITED.key
    ]);
  }

  /**
   * @param {Document} value
   */
  set estatePowerOfAttorneyPropertyAndPersonalCarePartnerEdited(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estatePowerOfAttorneyPropertyAndPersonalCareFinalUploaded() {
    return this.getValueForSubType([
      DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PROPERTY_AND_PERSONAL_CARE_FINAL_UPLOADED.key
    ]);
  }

  /**
   * @param {Document} value
   */
  set estatePowerOfAttorneyPropertyAndPersonalCareFinalUploaded(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estatePowerOfAttorneyPropertyAndPersonalCareSigned() {
    return this.getValueForSubType([
      DocumentSubType.ESTATE_POWER_OF_ATTORNEY_PROPERTY_AND_PERSONAL_CARE_SIGNED.key
    ]);
  }

  /**
   * @param {Document} value
   */
  set estatePowerOfAttorneyPropertyAndPersonalCareSigned(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estateProtectionMandate() {
    return this.getValueForSubType([DocumentSubType.ESTATE_PROTECTION_MANDATE.key]);
  }

  /**
   * @param {Document} value
   */
  set estateProtectionMandate(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estateProtectionMandateDocx() {
    return this.getValueForSubType([DocumentSubType.ESTATE_PROTECTION_MANDATE_DOCX.key]);
  }

  /**
   * @param {Document} value
   */
  set estateProtectionMandateDocx(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estateProtectionMandateEstateProfessionalEdited() {
    return this.getValueForSubType([
      DocumentSubType.ESTATE_PROTECTION_MANDATE_ESTATE_PROFESSIONAL_EDITED.key
    ]);
  }

  /**
   * @param {Document} value
   */
  set estateProtectionMandateEstateProfessionalEdited(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estateProtectionMandatePartnerEdited() {
    return this.getValueForSubType([DocumentSubType.ESTATE_PROTECTION_MANDATE_PARTNER_EDITED.key]);
  }

  /**
   * @param {Document} value
   */
  set estateProtectionMandatePartnerEdited(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estateProtectionMandateFinalUploaded() {
    return this.getValueForSubType([DocumentSubType.ESTATE_PROTECTION_MANDATE_FINAL_UPLOADED.key]);
  }

  /**
   * @param {Document} value
   */
  set estateProtectionMandateFinalUploaded(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get willGuideInstructions() {
    return this.getValueForSubType([DocumentSubType.ESTATE_WILL_GUIDE_INSTRUCTIONS.key]);
  }

  /**
   * @param {Document} value
   */
  set willGuideInstructions(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estateProtectionMandateTrueCertifiedCopy() {
    return this.getValueForSubType([
      DocumentSubType.ESTATE_PROTECTION_MANDATE_TRUE_CERTIFIED_COPY.key
    ]);
  }

  /**
   * @param {Document} value
   */
  set estateProtectionMandateTrueCertifiedCopy(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get estateProtectionMandateSigned() {
    return this.getValueForSubType([DocumentSubType.ESTATE_PROTECTION_MANDATE_SIGNED.key]);
  }

  /**
   * @param {Document} value
   */
  set estateProtectionMandateSigned(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get willDocument() {
    return this.getValueForSubType([DocumentSubType.ESTATE_WILL.key], (doc) => !doc.isRejected);
  }

  /**
   * @returns {?Document}
   */
  get willTrueCertifiedCopy() {
    return this.getValueForSubType([DocumentSubType.ESTATE_WILL_TRUE_CERTIFIED_COPY.key]);
  }

  /**
   * @param {Document} value
   */
  set willTrueCertifiedCopy(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get closingLetterDocument() {
    return this.getValueForSubType([DocumentSubType.ESTATE_CLOSING_LETTER.key]);
  }

  /**
   * @param {Document} value
   */
  set closingLetterDocument(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get codicilDocument() {
    return this.getValueForSubType([DocumentSubType.ESTATE_CODICILS.key]);
  }

  /**
   * @param {Document} value
   */
  set willDocument(value) {
    this.setValue(value);
  }

  /**
   * @param {Document} value
   */
  set codicilDocument(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get willDocumentSigned() {
    return this.getValueForSubType([DocumentSubType.ESTATE_WILL_SIGNED.key]);
  }

  /**
   * @param {Document} value
   */
  set willDocumentSigned(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get proofOfWillDocumentSigned() {
    return this.getValueForSubType([DocumentSubType.ESTATE_PROOF_OF_WILL_SIGNED.key]);
  }

  /**
   * @param {Document} value
   */
  set proofOfWillDocumentSigned(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get affidavitOfExecutionDocument() {
    return this.getValueForSubType([DocumentSubType.ESTATE_AFFIDAVIT_OF_EXECUTION.key]);
  }

  /**
   * @param {Document} value
   */
  set affidavitOfExecutionDocument(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get coverPageWillDocument() {
    return this.getValueForSubType([DocumentSubType.ESTATE_WILL_COVER_PAGE.key]);
  }

  /**
   * @param {Document} value
   */
  set coverPageWillDocument(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get frenchDocxWillDocument() {
    return this.getValueForSubType(
      [DocumentSubType.ESTATE_WILL_DOCX.key],
      (doc) => doc.meta.isLocaleFrench
    );
  }

  /**
   * @param {Document} value
   */
  set frenchDocxWillDocument(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get englishDocxWillDocument() {
    return this.getValueForSubType(
      [DocumentSubType.ESTATE_WILL_DOCX.key],
      (doc) => doc.meta.isLocaleEnglish
    );
  }

  /**
   * @param {Document} value
   */
  set englishDocxWillDocument(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get editedWillDocument() {
    return this.getValueForSubType([DocumentSubType.ESTATE_WILL_EDITED.key]);
  }

  /**
   * @param {Document} value
   */
  set editedWillDocument(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get editedWillEstateProfessionalEditedDocxDocument() {
    return this.getValueForSubType([
      DocumentSubType.ESTATE_WILL_ESTATE_PROFESSIONAL_EDITED_DOCX.key
    ]);
  }

  /**
   * @param {Document} value
   */
  set editedWillEstateProfessionalEditedDocxDocument(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get finalWillDocument() {
    return this.getValueForSubType([DocumentSubType.ESTATE_WILL_FINAL.key]);
  }

  /**
   * @param {Document} value
   */
  set finalWillDocument(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get finalUploadedWillDocument() {
    return this.getValueForSubType([DocumentSubType.ESTATE_WILL_FINAL_UPLOADED.key]);
  }

  /**
   * @param {Document} value
   */
  set finalUploadedWillDocument(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get affidavitOfExecutionDocumentSigned() {
    return this.getValueForSubType([DocumentSubType.ESTATE_AFFIDAVIT_OF_EXECUTION_SIGNED.key]);
  }

  /**
   * @param {Document} value
   */
  set affidavitOfExecutionDocumentSigned(value) {
    this.setValue(value);
  }

  get assetAppraisalDocument() {
    return this.getValueForSubType([DocumentSubType.ASSET_APPRAISAL.key]);
  }

  /**
   * @param {Document} value
   */
  set assetAppraisalDocument(value) {
    this.setValue(value);
  }

  get assetPropertyTitleDocument() {
    return this.getValueForSubType([DocumentSubType.ASSET_PROPERTY_TITLE.key]);
  }

  /**
   * @param {Document} value
   */
  set assetPropertyTitleDocument(value) {
    this.setValue(value);
  }

  get assetLandDeedDocument() {
    return this.getValueForSubType([DocumentSubType.ASSET_LAND_DEED.key]);
  }

  /**
   * @param {Document} value
   */
  set assetLandDeedDocument(value) {
    this.setValue(value);
  }

  get assetLandProofIncomeDocument() {
    return this.getValueForSubType([DocumentSubType.ASSET_LAND_PROOF_INCOME.key]);
  }

  /**
   * @param {Document} value
   */
  set assetLandProofIncomeDocument(value) {
    this.setValue(value);
  }

  /**
   * @param {Document} value
   */
  set assetFirearmLicenseDocument(value) {
    this.setValue(value);
  }

  get assetFirearmLicenseDocument() {
    return this.getValueForSubType([DocumentSubType.ASSET_FIREARM_LICENSE.key]);
  }

  get assetPolicyContractDocument() {
    return this.getValueForSubType([DocumentSubType.ASSET_POLICY_CONTRACT.key]);
  }

  /**
   * @param {Document} value
   */
  set assetPolicyContractDocument(value) {
    this.setValue(value);
  }

  get assetTaxReportDocument() {
    return this.getValueForSubType([DocumentSubType.ASSET_TAX_REPORT.key]);
  }

  /**
   * @param {Document} value
   */
  set assetTaxReportDocument(value) {
    this.setValue(value);
  }

  get assetBankStatementDocument() {
    return this.getValueForSubType([DocumentSubType.ASSET_BANK_STATEMENTS.key]);
  }

  /**
   * @param {Document} value
   */
  set assetBankStatementDocument(value) {
    this.setValue(value);
  }

  get assetFuneralPreArrangementDocument() {
    return this.getValueForSubType([DocumentSubType.ASSET_PRE_ARRANGEMENT_CONTRACT.key]);
  }

  /**
   * @param {Document} value
   */
  set assetFuneralPreArrangementDocument(value) {
    this.setValue(value);
  }

  get assetFuneralPreArrangementCemeteryDeedDocument() {
    return this.getValueForSubType([
      DocumentSubType.ASSET_PRE_ARRANGEMENT_CEMETERY_PROPERTY_DEED.key
    ]);
  }

  /**
   * @param {Document} value
   */
  set assetFuneralPreArrangementCemeteryDeedDocument(value) {
    this.setValue(value);
  }

  get assetStocksDocument() {
    return this.getValueForSubType([DocumentSubType.ASSET_STOCK_OPTIONS.key]);
  }

  /**
   * @param {Document} value
   */
  set assetStocksDocument(value) {
    this.setValue(value);
  }

  get liabilityAppraisalDocument() {
    return this.getValueForSubType([DocumentSubType.LIABILITY_APPRAISAL.key]);
  }

  /**
   * @param {Document} value
   */
  set liabilityAppraisalDocument(value) {
    this.setValue(value);
  }

  get liabilityBankStatementDocument() {
    return this.getValueForSubType([DocumentSubType.LIABILITY_BANK_STATEMENTS.key]);
  }

  /**
   * @param {Document} value
   */
  set liabilityBankStatementDocument(value) {
    this.setValue(value);
  }

  get liabilityBillDocument() {
    return this.getValueForSubType([DocumentSubType.LIABILITY_BILL.key]);
  }

  /**
   * @param {Document} value
   */
  set liabilityPotentialSpouseClaimDocument(value) {
    this.setValue(value);
  }

  get liabilityPotentialSpouseClaimDocument() {
    return this.getValueForSubType([DocumentSubType.LIABILITY_POTENTIAL_SPOUSE_CLAIM.key]);
  }

  /**
   * @param {Document} value
   */
  set liabilityBillDocument(value) {
    this.setValue(value);
  }

  get liabilityLeaseDocument() {
    return this.getValueForSubType([DocumentSubType.LIABILITY_LEASE.key]);
  }

  /**
   * @param {Document} value
   */
  set liabilityExecutorDisbursementDocument(value) {
    this.setValue(value);
  }

  get liabilityExecutorDisbursementDocument() {
    return this.getValueForSubType([DocumentSubType.LIABILITY_EXECUTOR_DISBURSEMENT_INVOICE.key]);
  }

  /**
   * @param {Document} value
   */
  set liabilityLeaseDocument(value) {
    this.setValue(value);
  }

  get liabilityTaxDocument() {
    return this.getValueForSubType([DocumentSubType.LIABILITY_TAX.key]);
  }

  /**
   * @param {Document} value
   */
  set liabilityTaxDocument(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get marriageCertificate() {
    return this.getValueForSubType([
      DocumentSubType.ESTATE_SCANNED_MARRIAGE_CERTIFICATE.key,
      DocumentSubType.ESTATE_CERTIFIED_TRUE_MARRIAGE_CERTIFICATE.key
    ]);
  }

  /**
   * @param {Document} value
   */
  set marriageCertificate(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get marriageContract() {
    return this.getValueForSubType([DocumentSubType.ESTATE_MARRIAGE_CONTRACT.key]);
  }

  /**
   * @param {Document} value
   */
  set marriageContract(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get domesticContract() {
    return this.getValueForSubType([DocumentSubType.ESTATE_DOMESTIC_CONTRACT.key]);
  }

  /**
   * @param {Document} value
   */
  set domesticContract(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get deathCertificateDocument() {
    return this.getValueForSubType([DocumentSubType.ESTATE_DEATH_CERTIFICATE.key]);
  }

  /**
   * @param {Document} value
   */
  set deathCertificateDocument(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get spouseDeathCertificateDocument() {
    return this.getValueForSubType([DocumentSubType.ESTATE_SPOUSE_DEATH_CERTIFICATE.key]);
  }

  /**
   * @param {Document} value
   */
  set spouseDeathCertificateDocument(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get divorceCertificateDocument() {
    return this.getValueForSubType([DocumentSubType.ESTATE_DIVORCE_CERTIFICATE.key]);
  }

  /**
   * @param {Document} value
   */
  set divorceCertificateDocument(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get noticeOfAssessmentDocument() {
    return this.getValueForSubType([DocumentSubType.TAX_NOTICE_OF_ASSESSMENT.key]);
  }

  /**
   * @param {Document} value
   */
  set noticeOfAssessmentDocument(value) {
    this.setValue(value);
  }

  /**
   * @returns {Document}
   */
  get taxOwingVoucherDocument() {
    return this.getValueForSubType([DocumentSubType.TAX_OWING_VOUCHER.key]);
  }

  /**
   * @returns {Document}
   */
  get bankingChequeDocument() {
    return this.getValueForSubType([DocumentSubType.BANKING_CHEQUE.key]);
  }

  /**
   * @returns {Document}
   */
  get bankingStatementDocument() {
    return this.getValueForSubType([DocumentSubType.BANKING_BANK_STATEMENT.key]);
  }

  /**
   * @param {Document} value
   */
  set bankingStatementDocument(value) {
    this.setValue(value);
  }

  /**
   * @param {Document} value
   */
  set taxOwingVoucherDocument(value) {
    this.setValue(value);
  }

  /**
   * @returns {Document}
   */
  get grantOfProbateDocument() {
    return this.getValueForSubType([DocumentSubType.ESTATE_GRANT_OF_PROBATE.key]);
  }

  /**
   * @param {Document} value
   */
  set grantOfProbateDocument(value) {
    this.setValue(value);
  }

  /**
   * @returns {Document}
   */
  get administrationLettersDocument() {
    return this.getValueForSubType([DocumentSubType.ESTATE_ADMINISTRATION_LETTERS.key]);
  }

  /**
   * @param {Document} value
   */
  set administrationLettersDocument(value) {
    this.setValue(value);
  }

  get probateDE172Document() {
    return this.getValueForSubType([DocumentSubType.ESTATE_PROBATE_DE_172.key]);
  }

  /**
   * @param {Document} value
   */
  set probateDE172Document(value) {
    this.setValue(value);
  }

  get probateDE174Document() {
    return this.getValueForSubType([DocumentSubType.ESTATE_PROBATE_DE_174.key]);
  }

  /**
   * @param {Document} value
   */
  set probateDE174Document(value) {
    this.setValue(value);
  }

  /**
   * @param {Document} value
   */
  set probateCommissionedDocument(value) {
    this.setValue(value);
  }

  /**
   * @returns {Document}
   */
  get probateCommissionedDocument() {
    return this.getValueForSubType([DocumentSubType.ESTATE_PROBATE_COMMISSIONED_DOCUMENT.key]);
  }

  /**
   * @param {Document} value
   */
  set einConfirmationLetterDocument(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get einConfirmationLetterDocument() {
    return this.getValueForSubType([DocumentSubType.ESTATE_EIN_CONFIRMATION_LETTER.key]);
  }

  /**
   * @returns {?Document}
   */
  get probateRefereeInvoice() {
    return this.getValueForSubType([DocumentSubType.LIABILITY_PROBATE_REFEREE_INVOICE.key]);
  }

  /**
   * @param {Document} value
   */
  set probateRefereeInvoice(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get coverLetterInventoryAndAppraisal() {
    return this.getValueForSubType([
      DocumentSubType.ESTATE_COVER_LETTER_INVENTORY_AND_APPRAISAL.key
    ]);
  }

  /**
   * @param {Document} value
   */
  set coverLetterInventoryAndAppraisal(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get probateApplicationForm() {
    return this.getValueForSubType([DocumentSubType.ESTATE_PROBATE_APPLICATION_FORM.key]);
  }

  /**
   * @param {Document} value
   */
  set probateApplicationForm(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get signedProbateApplicationForm() {
    return this.getValueForSubType([DocumentSubType.ESTATE_SIGNED_PROBATE_APPLICATION_FORM.key]);
  }

  /**
   * @param {Document} value
   */
  set signedProbateApplicationForm(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get petitionForProbateRejection() {
    return this.getValueForSubType([DocumentSubType.ESTATE_PETITION_FOR_PROBATE_REJECTION.key]);
  }

  /**
   * @param {Document} value
   */
  set petitionForProbateRejection(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get probateInformationForm() {
    return this.getValueForSubType([DocumentSubType.ESTATE_PROBATE_INFORMATION_FORM.key]);
  }

  /**
   * @param {Document} value
   */
  set probateInformationForm(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get cohabitationAgreement() {
    return this.getValueForSubType([DocumentSubType.COHABITATION_AGREEMENT.key]);
  }

  /**
   * @param {Document} value
   */
  set cohabitationAgreement(value) {
    this.setValue(value);
  }

  /**
   * @returns {?Document}
   */
  get financialInstitutionLetter() {
    return this.getValueForSubType([DocumentSubType.BANKING_FINANCIAL_INSTITUTION_LETTER.key]);
  }

  /**
   * @param {Document} value
   */
  set financialInstitutionLetter(value) {
    this.setValue(value);
  }

  /**
   * @param subTypes
   * @param additionalCheck
   * @returns {Document}
   */
  getValueForSubType(subTypes, additionalCheck = () => true) {
    return (
      this.get()?.find((doc) => subTypes.includes(doc.subType) && additionalCheck(doc)) ||
      new Document()
    );
  }

  /**
   * @private
   * @param {string} type
   * @returns {?Document}
   */
  getValueForType(type) {
    return this.getValueForSubType(
      DocumentSubType.getJurisdictionSubTypesForDocumentType(type).map((a) => a.key)
    );
  }

  /**
   * @private
   * @param {Document} value
   */
  setValue(value) {
    const index = this.get()?.findIndex((doc) => doc.subType === value.subType);
    this.add(value, index);
  }

  /**
   * @private
   * @param {DocumentForm} value
   */
  setValueByType(value) {
    const index = this.get()?.findIndex((doc) => doc.type === value.type);
    this.add(value, index);
  }
}
